/**
 * LocalMediaSheetContent
 *
 * Content component for the local-media viewer sheet. Renders only the
 * locally-picked screenshot (a `Blob`/`File`, not a remote URL) full-size via
 * `ContentThumbnail`, centered both horizontally and vertically in the sheet's
 * content area. The "Feedback screenshot" title and the "Close" CTA are
 * rendered by the sheet framework's native `title` / `cta` slots (wired in
 * `LocalMediaSheet.show`), not here.
 *
 * Owns the object-URL lifecycle: creates it once and revokes it on unmount
 * (sheet close) to avoid a leak.
 */

import React, { useEffect, useState } from 'react';
import { ContentThumbnail } from '@/presentation/feature/content/components/ContentThumbnail';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';

/**
 * Compute the centered, aspect-ratio-aware box for the local thumbnail.
 *
 * A simpler variant of `DefaultMediaPage.getThumbnailBoxStyle`: the box fills
 * the available width but caps its height at `maxHeight`
 * (`responsiveDesign.addFeedback.localMediaMaxHeight`) and derives a matching
 * `maxWidth` (`height * ratio`) so it keeps its aspect ratio (no distortion)
 * while never overflowing the sheet surface — for both landscape (`ratio > 1`)
 * and portrait (`ratio < 1`) media.
 */
const getThumbnailBoxStyle = (
  aspectRatio: number,
  maxHeight: string,
): React.CSSProperties => ({
  width: '100%',
  maxWidth: `calc(${maxHeight} * ${aspectRatio})`,
  aspectRatio: aspectRatio.toString(),
  maxHeight,
  margin: '0 auto',
});

interface LocalMediaSheetContentProps {
  file: Blob;
  aspectRatio: number;
}

/**
 * Renders the picked image. Owns the object-URL lifecycle: creates it once and
 * revokes it on unmount (sheet close) to avoid a leak.
 */
export const LocalMediaSheetContent: React.FC<LocalMediaSheetContentProps> = ({
  file,
  aspectRatio,
}) => {
  const responsive = useResponsiveDesign();
  // Create the object URL exactly once (lazy state initializer), then revoke it
  // when this content unmounts — which happens on sheet close.
  const [objectUrl] = useState(() => URL.createObjectURL(file));

  useEffect(() => {
    return () => {
      URL.revokeObjectURL(objectUrl);
    };
  }, [objectUrl]);

  return (
    <div
      {...qaAttr({ id: 'local-media-sheet', status: 'ready' })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        // Fill the sheet's `flex-1` content area so the single image box is
        // centered both horizontally and vertically in the available space.
        height: '100%',
        width: '100%',
        padding: `${responsive.spacing.base}px`,
      }}
    >
      <div
        style={getThumbnailBoxStyle(
          aspectRatio,
          responsive.addFeedback.localMediaMaxHeight,
        )}
      >
        <ContentThumbnail
          thumbnailUrl={objectUrl}
          qa={{ id: 'local-media-thumbnail' }}
        />
      </div>
    </div>
  );
};
